export type Id = string | number;

export interface ITreeItem {
	id: Id;
	parentId?: Id;
	$id?: number;
	nodeType?: string;
}

export interface ITree<T extends ITreeItem = ITreeItem> extends ITreeItem {
	children?: ITree[];
}

export function recursiveFlat(treeIn: ITree): ITreeItem[] {
	const root = { ...treeIn };
	delete root.children;
	if (treeIn.children) {
		return [root, ...treeIn.children.reduce((acc, cur) => [...acc, ...recursiveFlat(cur)], [])];
	}
	return [root];
}

// TODO: need tests!
export function flatTrees(trees: ITree[]): ITreeItem[] {
	return trees.reduce((acc, cur) => [...acc, ...recursiveFlat(cur)], []);
}

export function exceptItems<I = any>(
	except: ITreeItem[],
	items: ITreeItem[],
	idGetter?: (item: I) => Id,
	parentIdGetter?: (item: I) => Id
): ITreeItem[] {
	return items.filter(
		item =>
			!except.some(exc => (idGetter ? idGetter(exc as any) : exc.id) === (idGetter ? idGetter(item as any) : item.id))
	);
}

export function buildTree<I = any>(
	root: ITree,
	items: ITreeItem[],
	idGetter?: (item: I) => Id,
	parentIdGetter?: (item: I) => Id
): ITree {
	const filterChild = (item: ITreeItem) => {
		const parent = root;
		const itemParentId = parentIdGetter ? parentIdGetter(item as any) : item.parentId;
		const parentId = idGetter ? idGetter(parent as any) : parent.id;
		const isChild = itemParentId === parentId;
		return isChild;
	};
	root.children = items.filter(filterChild);
	const otherItems = exceptItems(root.children, items, idGetter, parentIdGetter);
	root.children.forEach(child => buildTree(child, otherItems, idGetter, parentIdGetter));
	return root;
}

export function buildTrees<I extends ITreeItem, T extends ITree>(
	items: I[],
	idGetter?: (item: I) => Id,
	parentIdGetter?: (item: I) => Id
): T[] {
	const roots = items.filter(item => (parentIdGetter ? !parentIdGetter(item) : !item.parentId));
	const notRoots = exceptItems(roots as any, items, idGetter, parentIdGetter);
	const trees = roots.reduce((acc, cur) => [...acc, buildTree(cur, notRoots, idGetter, parentIdGetter)], []);
	return trees;
}

export function buildTreeImmutable<I extends ITreeItem, M extends ITree<I>>(record: Record<string, I>): M[] {
	const menus: M[] = [];

	const createMenu: (i: I) => M = item => <any>{ id: item.id, parentId: item.parentId, children: [] };

	const items = Object.values(record);
	(items || []).forEach(item => {
		if (!Boolean(item.parentId)) {
			menus.push(createMenu(item));
		}
	});

	const fillMenu: (menu: M) => M = menu => {
		const children = items.filter(item => item.parentId === menu.id);
		const fillChildren = children.map(createMenu).map(fillMenu);
		menu.children = fillChildren;
		return menu;
	};

	return menus.map(menu => fillMenu(menu));
}
